import { useCallback } from 'react'
import cx from 'classnames'
import styles from './Checkbox.module.css'
import { Spinner } from '@/components/Spinner'
import { Icon } from '@/components/Icon'

export interface CheckboxProps {
  checked?: boolean | 'indeterminate'
  isDisabled?: boolean
  isPending?: boolean
  onChange?(checked: boolean): unknown
  muteClickEvent?: boolean
  size?: number
  'aria-label'?: string
  testId?: string
}

/**
 * A checkbox, which can also show an indeterminate or loading state.
 */
export function Checkbox({
  isDisabled,
  isPending,
  onChange,
  muteClickEvent = false,
  checked = false,
  size = 24,
  'aria-label': ariaLabel,
  testId,
}: CheckboxProps) {
  const change = useCallback(() => {
    if (!isDisabled) {
      onChange?.(checked === 'indeterminate' ? true : !checked)
    }
  }, [onChange, checked, isDisabled])

  return (
    <span
      role="checkbox"
      aria-disabled={isDisabled ? 'true' : 'false'}
      aria-checked={
        checked ? (checked === 'indeterminate' ? 'mixed' : 'true') : 'false'
      }
      className={cx(
        styles.root,
        isDisabled && styles.disabled,
        isPending && styles.loading,
      )}
    >
      <input
        className={styles.input}
        type="checkbox"
        checked={checked === true}
        onChange={change}
        onClick={(e) => muteClickEvent && e.stopPropagation()}
        aria-label={ariaLabel}
        data-test={testId}
      />
      {isPending ? (
        <Spinner size={20} />
      ) : (
        <Icon
          color="var(--theme-base)"
          name={
            checked
              ? checked === 'indeterminate'
                ? 'checkbox-indeterminate'
                : 'checkbox'
              : 'checkbox-blank'
          }
          size={size}
        />
      )}
    </span>
  )
}
